import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Container,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Divider,
  Chip
} from '@mui/material';
import {
  Compare as CompareIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Star as StarIcon,
  Article as ArticleIcon,
  QuestionAnswer as QuestionAnswerIcon,
  PhotoCamera as PhotoCameraIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Remove as RemoveIcon2
} from '@mui/icons-material';
import api from '../services/api';

const ComparisonTool = () => {
  const [searchParams] = useSearchParams();
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    const fetchComparison = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const snapshot1Id = searchParams.get('snapshot1');
        const snapshot2Id = searchParams.get('snapshot2');
        
        if (!snapshot1Id || !snapshot2Id) {
          setError('Missing snapshot IDs for comparison');
          return;
        }
        
        const response = await api.get(`/api/snapshots/compare/${snapshot1Id}/${snapshot2Id}`);
        setComparison(response.data);
      } catch (error) {
        console.error('Error fetching comparison:', error);
        setError('Failed to load comparison data');
      } finally {
        setLoading(false);
      }
    };

    fetchComparison();
  }, [searchParams]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <TrendingUpIcon color="success" />;
    if (change < 0) return <TrendingDownIcon color="error" />;
    return <RemoveIcon color="action" />;
  };

  const getChangeColor = (change) => {
    if (change > 0) return 'success.main';
    if (change < 0) return 'error.main';
    return 'text.secondary';
  };



  const getDataIcon = (dataType) => {
    switch (dataType) {
      case 'reviews': return <StarIcon />;
      case 'posts': return <ArticleIcon />;
      case 'qa': return <QuestionAnswerIcon />;
      case 'photos': return <PhotoCameraIcon />;
      case 'listing': return <BusinessIcon />;
      default: return null;
    }
  };

  const getDataTitle = (dataType) => {
    switch (dataType) {
      case 'reviews': return 'Reviews';
      case 'posts': return 'Posts';
      case 'qa': return 'Q&A';
      case 'photos': return 'Photos';
      case 'listing': return 'Business Information';
      default: return dataType;
    }
  };

  const generateDiff = (obj1, obj2, path = '') => {
    const diff = {
      added: [],
      removed: [],
      changed: []
    };

    const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

    allKeys.forEach(key => {
      const currentPath = path ? `${path}.${key}` : key;
      const val1 = obj1?.[key];
      const val2 = obj2?.[key];

      if (val1 === undefined && val2 !== undefined) {
        // Added in obj2
        diff.added.push({
          path: currentPath,
          value: val2
        });
      } else if (val1 !== undefined && val2 === undefined) {
        // Removed from obj1
        diff.removed.push({
          path: currentPath,
          value: val1
        });
      } else if (JSON.stringify(val1) !== JSON.stringify(val2)) {
        if (typeof val1 === 'object' && typeof val2 === 'object' && val1 !== null && val2 !== null) {
          // Recursively compare nested objects
          const nestedDiff = generateDiff(val1, val2, currentPath);
          diff.added.push(...nestedDiff.added);
          diff.removed.push(...nestedDiff.removed);
          diff.changed.push(...nestedDiff.changed);
        } else {
          // Values are different
          diff.changed.push({
            path: currentPath,
            oldValue: val1,
            newValue: val2
          });
        }
      }
    });

    return diff;
  };

  const renderDiffView = (dataType) => {
    if (!comparison) return null;

    const data1 = comparison.snapshot1.rawData?.[dataType];
    const data2 = comparison.snapshot2.rawData?.[dataType];
    
    if (!data1 && !data2) {
      return (
        <Alert severity="info">
          No data available for {getDataTitle(dataType)} in either snapshot
        </Alert>
      );
    }

    const diff = generateDiff(data1, data2);

    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getDataIcon(dataType)}
          {getDataTitle(dataType)} Differences
        </Typography>

        {/* Summary */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Chip 
                icon={<AddIcon />} 
                label={`${diff.added.length} Added`} 
                color="success" 
                variant="outlined"
              />
            </Grid>
            <Grid item xs={4}>
              <Chip 
                icon={<RemoveIcon2 />} 
                label={`${diff.removed.length} Removed`} 
                color="error" 
                variant="outlined"
              />
            </Grid>
            <Grid item xs={4}>
              <Chip 
                icon={<CompareIcon />} 
                label={`${diff.changed.length} Changed`} 
                color="warning" 
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Box>

                 {/* Added Items */}
         {diff.added.length > 0 && (
           <Box sx={{ mb: 3 }}>
             <Typography variant="h6" color="success.main" gutterBottom>
               ✅ Added Items
             </Typography>
             {diff.added.map((item, index) => (
               <Paper key={index} sx={{ p: 2, mb: 1, bgcolor: '#1a1a1a', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
                 <Typography variant="subtitle2" gutterBottom sx={{ color: '#4caf50' }}>
                   {item.path}
                 </Typography>
                 <pre style={{ 
                   margin: 0, 
                   fontSize: '12px', 
                   fontFamily: 'monospace',
                   whiteSpace: 'pre-wrap',
                   wordBreak: 'break-word',
                   color: '#e0e0e0',
                   backgroundColor: '#2d2d2d',
                   padding: '12px',
                   borderRadius: '4px',
                   border: '1px solid rgba(76, 175, 80, 0.2)'
                 }}>
                   {JSON.stringify(item.value, null, 2)}
                 </pre>
               </Paper>
             ))}
           </Box>
         )}

         {/* Removed Items */}
         {diff.removed.length > 0 && (
           <Box sx={{ mb: 3 }}>
             <Typography variant="h6" color="error.main" gutterBottom>
               ❌ Removed Items
             </Typography>
             {diff.removed.map((item, index) => (
               <Paper key={index} sx={{ p: 2, mb: 1, bgcolor: '#1a1a1a', border: '1px solid rgba(244, 67, 54, 0.3)' }}>
                 <Typography variant="subtitle2" gutterBottom sx={{ color: '#f44336' }}>
                   {item.path}
                 </Typography>
                 <pre style={{ 
                   margin: 0, 
                   fontSize: '12px', 
                   fontFamily: 'monospace',
                   whiteSpace: 'pre-wrap',
                   wordBreak: 'break-word',
                   color: '#e0e0e0',
                   backgroundColor: '#2d2d2d',
                   padding: '12px',
                   borderRadius: '4px',
                   border: '1px solid rgba(244, 67, 54, 0.2)'
                 }}>
                   {JSON.stringify(item.value, null, 2)}
                 </pre>
               </Paper>
             ))}
           </Box>
         )}

         {/* Changed Items */}
         {diff.changed.length > 0 && (
           <Box sx={{ mb: 3 }}>
             <Typography variant="h6" color="warning.main" gutterBottom>
               🔄 Changed Items
             </Typography>
             {diff.changed.map((item, index) => (
               <Paper key={index} sx={{ p: 2, mb: 1, bgcolor: '#1a1a1a', border: '1px solid rgba(255, 152, 0, 0.3)' }}>
                 <Typography variant="subtitle2" gutterBottom sx={{ color: '#ff9800' }}>
                   {item.path}
                 </Typography>
                 <Grid container spacing={2}>
                   <Grid item xs={6}>
                     <Typography variant="body2" color="error.main" gutterBottom>
                       Old Value:
                     </Typography>
                     <pre style={{ 
                       margin: 0, 
                       fontSize: '12px', 
                       fontFamily: 'monospace',
                       whiteSpace: 'pre-wrap',
                       wordBreak: 'break-word',
                       color: '#e0e0e0',
                       backgroundColor: '#2d2d2d',
                       padding: '12px',
                       borderRadius: '4px',
                       border: '1px solid rgba(244, 67, 54, 0.2)'
                     }}>
                       {JSON.stringify(item.oldValue, null, 2)}
                     </pre>
                   </Grid>
                   <Grid item xs={6}>
                     <Typography variant="body2" color="success.main" gutterBottom>
                       New Value:
                     </Typography>
                     <pre style={{ 
                       margin: 0, 
                       fontSize: '12px', 
                       fontFamily: 'monospace',
                       whiteSpace: 'pre-wrap',
                       wordBreak: 'break-word',
                       color: '#e0e0e0',
                       backgroundColor: '#2d2d2d',
                       padding: '12px',
                       borderRadius: '4px',
                       border: '1px solid rgba(76, 175, 80, 0.2)'
                     }}>
                       {JSON.stringify(item.newValue, null, 2)}
                     </pre>
                   </Grid>
                 </Grid>
               </Paper>
             ))}
           </Box>
         )}

        {diff.added.length === 0 && diff.removed.length === 0 && diff.changed.length === 0 && (
          <Alert severity="info">
            No differences found in {getDataTitle(dataType)} data
          </Alert>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!comparison) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">
          No comparison data available
        </Alert>
      </Container>
    );
  }

  const dataTypes = ['reviews', 'posts', 'qa', 'photos', 'listing'];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant="h2" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            mb: 2
          }}
          gutterBottom
        >
          Snapshot Comparison
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.9)',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
          }}
        >
          Compare data changes between snapshots
        </Typography>
      </Box>

      {/* Summary Section */}
      <Card className="modern-card" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompareIcon color="primary" />
            Summary
          </Typography>
          
          {/* Snapshot Information */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Snapshot 1 ({formatDate(comparison.snapshot1.captureDate)})
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Profile ID: {comparison.snapshot1.profileId}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Snapshot 2 ({formatDate(comparison.snapshot2.captureDate)})
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Profile ID: {comparison.snapshot2.profileId}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Changes Overview */}
          <Typography variant="subtitle1" gutterBottom>
            Data Changes
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  {getChangeIcon(comparison.differences.reviews)}
                </Box>
                <Typography variant="h4" sx={{ color: getChangeColor(comparison.differences.reviews), fontWeight: 'bold' }}>
                  {comparison.differences.reviews > 0 ? '+' : ''}{comparison.differences.reviews}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Reviews
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  {getChangeIcon(comparison.differences.posts)}
                </Box>
                <Typography variant="h4" sx={{ color: getChangeColor(comparison.differences.posts), fontWeight: 'bold' }}>
                  {comparison.differences.posts > 0 ? '+' : ''}{comparison.differences.posts}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Posts
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  {getChangeIcon(comparison.differences.qa)}
                </Box>
                <Typography variant="h4" sx={{ color: getChangeColor(comparison.differences.qa), fontWeight: 'bold' }}>
                  {comparison.differences.qa > 0 ? '+' : ''}{comparison.differences.qa}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Q&A
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  {getChangeIcon(comparison.differences.photos)}
                </Box>
                <Typography variant="h4" sx={{ color: getChangeColor(comparison.differences.photos), fontWeight: 'bold' }}>
                  {comparison.differences.photos > 0 ? '+' : ''}{comparison.differences.photos}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Photos
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>



      {/* Raw Data Comparison */}
      <Card className="modern-card">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompareIcon color="primary" />
            Detailed Differences
          </Typography>
          
          <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} sx={{ mb: 3 }}>
            {dataTypes.map((dataType, index) => (
              <Tab 
                key={dataType}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getDataIcon(dataType)}
                    {getDataTitle(dataType)}
                  </Box>
                }
                iconPosition="start"
              />
            ))}
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {dataTypes.map((dataType, index) => (
              selectedTab === index && (
                <Box key={dataType}>
                  {renderDiffView(dataType)}
                </Box>
              )
            ))}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ComparisonTool; 